'use client';

import clsx from 'clsx';
import { useCart } from '@/components/commerce/cart/cart-context';
import { useThrottle } from '@/lib/hooks/use-throttle';
import {
  elevarAddToCart,
  elevarRemoveFromCart
} from '@/lib/elevar/cart-events';
import { gtmAddToCart } from '@/utils/events/gtmEvents';

const SvgSubtract = () => (
  <svg
    width="20px"
    height="20px"
    viewBox="0 0 22 22"
    version="1.1"
    xmns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    aria-hidden="true"
  >
    <g
      stroke="none"
      strokeWidth="1"
      fill="none"
      fillRule="evenodd"
      strokeDasharray="0,0"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <g transform="translate(-1111.000000, -191.000000)" stroke="#000000">
        <g transform="translate(1112.000000, 191.000000)">
          <g transform="translate(0.000000, 1.000000)">
            <path
              d="M20,10 C20,4.47715229 15.5228477,0 10,0 C4.47715229,0 0,4.47715229 0,10 C0,15.5228477 4.47715229,20 10,20 C15.5228477,20 20,15.5228477 20,10 Z"
              strokeWidth="0.5"
              fillRule="nonzero"
            ></path>
            <line
              x1="5.55555556"
              y1="10"
              x2="14.4444444"
              y2="10"
              strokeWidth="0.75"
            ></line>
          </g>
        </g>
      </g>
    </g>
  </svg>
);

const SvgAdd = () => (
  <svg
    width="20px"
    height="20px"
    viewBox="0 0 22 22"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    aria-hidden="true"
  >
    <g
      stroke="none"
      strokeWidth="1"
      fill="none"
      fillRule="evenodd"
      strokeDasharray="0,0"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <g transform="translate(-1164.000000, -191.000000)" stroke="#000000">
        <g transform="translate(1112.000000, 191.000000)">
          <g transform="translate(53.000000, 1.000000)">
            <path
              d="M20,10 C20,4.47715229 15.5228477,0 10,0 C4.47715229,0 0,4.47715229 0,10 C0,15.5228477 4.47715229,20 10,20 C15.0384103,20 19.2065263,16.2738289 19.8989749,11.4268598 C19.965553,10.9608293 20,10.4844374 20,10 Z"
              strokeWidth="0.5"
              fillRule="nonzero"
            ></path>
            <line
              x1="5.55555556"
              y1="10"
              x2="14.4444444"
              y2="10"
              strokeWidth="0.75"
            ></line>
            <line
              x1="10.2"
              y1="14.4444444"
              x2="10"
              y2="5.55555556"
              strokeWidth="0.75"
            ></line>
          </g>
        </g>
      </g>
    </g>
  </svg>
);

function updateQuantity(lineItemToUpdate, cart, type) {
  const mainQuantity = lineItemToUpdate.quantity;

  // Returns an attribute's value by exact match or substring match
  const getAttributeValue = (item, key, useIncludes = false) => {
    const attribute = item.attributes.find((attr) =>
      useIncludes ? attr.key.includes(key) : attr.key === key
    );
    return attribute ? attribute.value : undefined;
  };

  // Updates a child's quantity based on its promo status
  const updateQtyBasedOnPromo = (child) => {
    const isPromo = child.attributes.some(
      (attr) => attr.key.includes('isPromo') && attr.value === 'true'
    );
    if (isPromo) {
      const gwpQuantity =
        parseInt(getAttributeValue(child, 'gwpQuantity', true)) || 1;
      child.quantity = mainQuantity * gwpQuantity;
    } else {
      child.quantity = mainQuantity;
    }
    return child;
  };

  // Filter child items associated with the main product and update their qtys
  const updatedChildItems = cart.lines
    .filter((item) => {
      // We only want to update items associated with the main product
      if (item.id === lineItemToUpdate.id) {
        return false;
      }

      // Handle both gwp and bundle promotions
      const promoParentValue = getAttributeValue(item, '_promoParent');
      const bundleId = getAttributeValue(item, '_bundleId');
      const productBundleId = getAttributeValue(lineItemToUpdate, '_bundleId');
      const matchesBundleId =
        bundleId !== undefined && bundleId == productBundleId;
      return (
        promoParentValue == lineItemToUpdate.merchandise.id || matchesBundleId
      );
    })
    .map(updateQtyBasedOnPromo);

  if (type == 'plus') {
    elevarAddToCart([...updatedChildItems, lineItemToUpdate], 1);
    gtmAddToCart({
      itemsToAdd: [...updatedChildItems, lineItemToUpdate],
      page: 'Cart',
      quantity: 1
    });
  } else {
    elevarRemoveFromCart([...updatedChildItems, lineItemToUpdate], 1);
  }

  // Update the main product's quantity in the cart
  cart.lines = cart.lines.map((item) => {
    if (item.id === lineItemToUpdate.id) {
      item.quantity = mainQuantity;
    }
    return item;
  });

  // Build and return the update array containing the main product and its
  // updated child items
  return [
    lineItemToUpdate,
    ...updatedChildItems.map((child) => ({
      id: child.id,
      quantity: Number(child.quantity)
    }))
  ];
}

export function EditItemQuantityButton({ item, type, position }) {
  const { cart, updateCartItem } = useCart();

  const handleIncrement = async (lineItem) => {
    const updatedItems = updateQuantity(lineItem, cart, 'plus');
    const result = await updateCartItem(updatedItems, 'plus');

    if (result?.error) {
      console.error('Error updating cart item quantity:', result.error);
    }

    return updatedItems;
  };

  const handleDecrement = async (lineItem) => {
    const updatedItems = updateQuantity(lineItem, cart, 'minus');
    const result = await updateCartItem(updatedItems, 'minus');

    if (result?.error) {
      console.error('Error updating cart item quantity:', result.error);
    }

    return updatedItems;
  };

  const handleClick = useThrottle(() => {
    const currentItem = cart?.lines?.find((line) => line.id === item.id);
    if (!currentItem || !currentItem?.id) {
      return;
    }

    const newQuantity =
      type === 'plus' ? currentItem.quantity + 1 : currentItem.quantity - 1;

    if (type === 'plus') {
      handleIncrement(currentItem, newQuantity);
    } else if (type === 'minus') {
      handleDecrement(currentItem, newQuantity);
    }
  }, 200); /* Up to five requests per second */

  const show_quantity_buttons =
    item.attributes?.find((attribute) => attribute.key.includes('promoType'))
      ?.value !== 'free';

  return (
    <>
      {show_quantity_buttons ? (
        <button
          onClick={handleClick}
          aria-label={
            type === 'plus'
              ? `Increase item quantity ${item?.merchandise?.product?.title} ${item?.merchandise?.title}`
              : `Reduce item quantity ${item?.merchandise?.product?.title} ${item?.merchandise?.title}`
          }
          data-cy={`${type}-qty`} // minus-qty | plus-qty
          className={clsx('h-6 w-6 cursor-pointer justify-self-center', {
            'ml-auto': type === 'minus'
          })}
        >
          {type === 'plus' ? (
            <SvgAdd position={position} />
          ) : (
            <SvgSubtract position={position} />
          )}
        </button>
      ) : (
        ''
      )}
    </>
  );
}
